// GENERADO — no editar a mano. Origen: backend/src/oficina/compartido/.
// Regenerar: node scripts/gen-oficina-compartido.mjs

// Vocabulario único de la oficina v2 (docs/oficina-v2/DISENO-OFICINA-V2.html §2, §6, §8).
// Código PURO y sin imports: se copia idéntico a la web con scripts/gen-oficina-compartido.mjs
// (lockstep en backend/tests/oficina/compartido-lockstep.test.ts). Editar solo aquí.

export const CICLOS = ['llegando', 'activo', 'cerrando', 'cierre_atascado', 'cerrado', 'despedido'] as const;
export type Ciclo = (typeof CICLOS)[number];
/**
 * WS15-D8 — lo que entrega un agente por la herramienta del informe: al INTEGRAR una parte es su «nota de entrega» (y
 * sigue); al IRSE, su «informe de cierre». Pau, 17-10: llamar igual a las dos «me confunde».
 */
export type ModoInforme = 'integrar' | 'cerrar';

export const FASES = ['orientandose', 'esperando_contrato', 'trabajando', 'verificando', 'bloqueado', 'cerrando', 'terminado'] as const;
export type Fase = (typeof FASES)[number];

export const SALUDES = ['verde', 'ambar', 'rojo'] as const;
export type Salud = (typeof SALUDES)[number];

export const TIPOS_TRABAJO = ['investigar', 'cambiar'] as const;
export type TipoTrabajo = (typeof TIPOS_TRABAJO)[number];

export const POTENCIAS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type Potencia = (typeof POTENCIAS)[number];
export const POTENCIA_EN_CRISTIANO: Record<Potencia, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  xhigh: 'Muy alta',
  max: 'Máxima',
};

export const CONTRATO_ESTADOS = ['sin_proponer', 'propuesto', 'aprobado', 'cambios_pedidos'] as const;
export type ContratoEstado = (typeof CONTRATO_ESTADOS)[number];

/** Límites de lo que escribe Pau: los usan los esquemas del servidor y los campos de la web. */
export const LIMITES = {
  caja: 50_000,
  respuestaFormulario: 600,
  noTocar: 1_000,
  nombre: 16,
  tema: 48,
  /** 04-11 — el nombre de un grupo de mesas («Precios», «Esperando a Santi»): 1–24 letras (migración 108). */
  grupo: 24,
  respuestaContrato: 1_000,
  nota: 500,
  texto: 4_000,
} as const;

export const ACCIONES_PERMISO = ['integrar', 'desplegar_pruebas', 'desplegar_produccion', 'comando', 'mensaje_externo', 'gasto'] as const;
export type AccionPermiso = (typeof ACCIONES_PERMISO)[number];

/**
 * 27-10 (Pau: «estas tarjetas me llenan el chat de permisos; la gente me pide permisos y se pone a hacer cosas sin haber
 * tenido ni que hablar»): pedir_permiso es solo para lo que hace la oficina. Lo que es de Pau (gasto, lo irreversible,
 * terceros) se le pide hablando en el chat y el agente espera su sí escrito: el «sí» de la tarjeta no frenaba nada (el
 * agente tiene las llaves y lo hace él) y la tarjeta se quedaba colgada cuando el chat avanzaba. Producción sigue en
 * la bandeja (el Allow sí frena: lo lanza la oficina). ADR D-036.
 */
export const ACCIONES_PEDIR = ['integrar', 'desplegar_pruebas', 'desplegar_produccion'] as const satisfies readonly AccionPermiso[];
/** Las acciones que antes del 27-10 abrían una tarjeta y ahora se piden en el chat (quedan en el historial). */
export const ACCIONES_POR_CHAT = ['comando', 'mensaje_externo', 'gasto'] as const satisfies readonly AccionPermiso[];

/**
 * 09-10 (Pau aprobó «aprobar desde la lista»): los permisos que se pueden aprobar con un «Sí» en la lista, sin abrir
 * el chat. Solo lo sencillo: subir a pruebas. Nunca producción, gasto, integrar (lo cierra la revisión), un mensaje
 * a fuera (llega a terceros) ni una orden suya («comando»: el reglamento solo le deja pedirla para lo irreversible):
 * esos se leen enteros en el chat antes de decir que sí.
 */
export const ACCIONES_PERMISO_RAPIDO = ['desplegar_pruebas'] as const satisfies readonly AccionPermiso[];

export const ESTADOS_VISUALES = ['llegando', 'saliendo', 'error', 'te_necesita', 'bloqueado', 'cerrando', 'trabajando', 'esperando'] as const;
export type EstadoVisual = (typeof ESTADOS_VISUALES)[number];

/** Estado del proceso del agente tal como lo guarda `agents.status`. */
/** S12 — qué le toca a Pau con un contratado, de mayor a menor peso (estado-visual.ts `situacion`). */
export type Situacion =
  | 'llegando'
  | 'autorizacion' // 1 · pide tu permiso (producción, gasto, irreversible)
  | 'bloqueado' // 2 · no puede seguir sin ti
  | 'te_escribio' // 3 · te toca contestar (o te dejó una pregunta/plan que no bloquea)
  | 'problema' // 4 · fallo técnico
  | 'trabajando' // 5
  | 'cerrando' // 6 · la oficina comprueba, revisa o integra
  | 'sin_pendiente' // 7
  | 'saliendo';

export type AgentStatus = 'running' | 'waiting' | 'finished' | 'error' | 'interrupted' | 'stuck';

/** Una sala de la oficina (= un proyecto habilitado). */
export interface SalaDTO {
  projectId: string;
  nombre: string;
  orden: number;
  principal: boolean;
  color: string;
}

/** Lo que la escena, la lista y el Expediente necesitan de cada contratado. Nada técnico. */
export interface ContratadoDTO {
  agentId: string;
  projectId: string;
  nombre: string;
  /**
   * 04-11 — el NOMBRE CORTO, como lo llama Pau en su mesa («PRECIOS», «CALYPSO 7»): lo propone la recepción al contratar
   * y Pau lo cambia con el lápiz (nunca lo pisa nadie más). Los de antes del 04-11 pueden tener aquí una frase.
   */
  tema: string;
  /** 04-11 — la frase larga de la recepción («Revisar los precios mínimos del CRM»); null si no la hay. */
  titulo: string | null;
  /** 04-11 — para qué está, en una frase fija escrita al contratar (panel derecho del chat); null si aún no la hay. */
  paraQue: string | null;
  /** 04-11 — quién lo trajo: una persona (ver `dueno`), la oficina sola (el conserje) o un contrato con Santi. */
  pedidoPor: 'persona' | 'oficina' | 'santi';
  /** 04-11 — si duerme hasta una hora (espera algo que corre solo), cuándo vuelve a mirar (ISO); null si no. */
  vuelveA: string | null;
  puesto: string;
  tipoTrabajo: TipoTrabajo;
  semillaAvatar: string;
  modelo: string;
  potencia: Potencia;
  ciclo: Ciclo;
  /**
   * 04-11 — el grupo de mesas en el que está (GrupoDTO.id); null = «Sin grupo». Uno o ninguno, nunca dos. Opcional en el
   * espejo: un servidor de antes de la 108 no lo manda (= sin grupo).
   */
  grupoId?: string | null;
  /** Su sitio DENTRO de su grupo (o de «Sin grupo»), en orden de lectura (compartido/mesas.ts). */
  mesa: number;
  fase: Fase;
  salud: Salud;
  titular: string;
  resumenParaPau: string;
  /** S12 — qué le toca a Pau con este agente (estado-visual.ts `situacion`). */
  situacion: Situacion;
  /** S12 — lo que pide, en sus palabras (autorización, lo que necesita para seguir, su pregunta); '' si nada. */
  pide: string;
  /**
   * 09-10 — el permiso que Pau puede aprobar desde la lista («Sí» sin abrir el chat): solo si tiene UNO pendiente y es
   * sencillo (ACCIONES_PERMISO_RAPIDO). null en cualquier otro caso (ninguno, varios, o de los que se leen enteros).
   */
  permisoRapido: { id: string; accion: (typeof ACCIONES_PERMISO_RAPIDO)[number] } | null;
  /** S12 — en qué paso del cierre está (solo con situacion «cerrando»). */
  pasoCierre: 'esperando_informe' | 'comprobando' | 'revisando' | 'esperando_permiso' | 'publicando' | 'en_cola' | 'archivando' | null;
  /** 30-09 — para qué es ese recorrido: «integrar» = al quedar integrado sigue trabajando; «cerrar» = se va. */
  modoCierre: 'cerrar' | 'integrar' | null;
  /** Lo que el agente dice que necesita de Pau para seguir (fase «bloqueado»); '' si nada o ya contestado. */
  bloqueo: string;
  visual: EstadoVisual;
  /** Qué necesita de Pau (solo con visual «te_necesita»). */
  teNecesita: 'atasco' | 'pregunta' | 'integrar' | 'permiso' | 'plan' | 'bloqueo' | null;
  sinNoticias: boolean;
  creadoEn: string;
  ultimoInformeEn: string | null;
  /** Su último mensaje en el chat (vista previa, como Telegram) y cuándo. null si aún no ha escrito. */
  ultimoMensaje: { texto: string; en: string } | null;
  /**
   * Desde cuándo te necesita: la hora de su última petición (propuesta, pregunta o permiso). La web deja de
   * gritar «te necesita» cuando Pau ya ha abierto el chat después de esa hora (29-09: «siempre están en te
   * necesitan»): la petición sigue en el chat, pero ya la ha visto. null si no pidió nada.
   */
  teNecesitaDesde: string | null;
  /**
   * 30-09 — le toca a Pau y aún no lo ha leído: EL mismo «no leído» del panel (el punto de la barra lateral,
   * la campana, Telegram), guardado en el servidor. Se enciende solo cuando el agente le devuelve la palabra
   * (acaba un turno que le contestaba) o le pide algo (permiso, decisión, plan, bloqueo); nunca mientras
   * trabaja y narra. Se apaga cuando Pau abre su chat o le escribe. Ver docs/oficina-v2/no-leido.md.
   */
  noLeido: boolean;
  /** 30-09 — Pau lo ha fijado arriba (favorito): el mismo «fijado» que la barra lateral del panel. */
  fijado: boolean;
  /** 30-09 — el icono (emoji) de su encargo; null si aún no lo ha elegido la recepción. */
  icono: string | null;
  /**
   * 06-10 — de quién es (quien lo contrató): sale en SU bandeja. `cuentaId` null = de Pau (el titular); si no, la
   * cuenta del socio y su nombre. Ver docs/cuentas-personales.md, «Bandejas por persona».
   */
  dueno: { cuentaId: string | null; nombre: string };
  /**
   * 04-11 — aparcar (aparcar.ts): los hechos con los que se calcula al leer si duerme. Lo último de Pau a este agente
   * (mensaje o permiso contestado), y las dos intenciones a mano de Pau: aparcarlo y despertarlo (gana la más nueva).
   */
  ultimoDePauEn: string | null;
  aparcadoEn: string | null;
  despertadoEn: string | null;
}

/**
 * 04-11 — un grupo de mesas («mesa de equipo», web/qa/auditoria-apple/propuesta-2/fuentes/grupos.md): una isla con nombre
 * dentro de una sala. Sus agentes son los contratados con `grupoId` igual a su id. Si espera a Santi NO va aquí: lo dice
 * siempre la pelota del contrato atado (`esperaTemaId`, de la lista de contratos), nunca una casilla guardada.
 */
export interface GrupoDTO {
  id: string;
  projectId: string;
  nombre: string;
  /** Posición en la sala (0 = primero); los grupos van en este orden y «Sin grupo» al final. */
  orden: number;
  /** El contrato con Santi atado (tema de pelotas), o null. */
  esperaTemaId: number | null;
  /**
   * Se quedó sin nadie vivo dentro (desde cuándo); null = tiene gente. Un grupo vacío no se pinta: solo se ofrece su
   * nombre 7 días como sugerencia (en «Mover a» y al contratar). Pasados los 7 días el servidor ya no lo manda.
   */
  vacioDesde: string | null;
}

export interface EstadoOficinaDTO {
  salas: SalaDTO[];
  contratados: ContratadoDTO[];
  /** 04-11 — los grupos de mesas de las salas activas. Opcional en el espejo: un servidor de antes no lo manda. */
  grupos?: GrupoDTO[];
  servidoEn: string;
}

/** Un criterio del contrato con su estado según el último parte del agente. */
export interface CriterioDTO {
  id: string;
  texto: string;
  enCristiano: string;
  estado: 'pendiente' | 'en_curso' | 'cumplido' | 'fallando' | 'aparcado';
  evidencia: string;
}

export interface PreguntaDTO {
  id: string;
  pregunta: string;
  contexto: string;
  opciones: { id: string; texto: string }[];
  recomendacion: string | null;
  porQueRecomienda: string;
  bloquea: boolean;
  fueraDeEncargo: boolean;
  en: string;
  /** 29-10 — la hace la oficina (zona ocupada, «¿lo cierro?» de antes), no el agente: solo estas siguen con botones. */
  deLaOficina?: boolean;
}

export interface PermisoDTO {
  id: string;
  accion: AccionPermiso;
  queHara: string;
  porQue: string;
  riesgo: string;
  comoSeDeshace: string;
  estado: 'pendiente' | 'aprobado' | 'denegado' | 'ejecutando' | 'hecho' | 'fallo' | 'retirado';
  creadoEn: string;
}

export interface EntradaHistorialDTO {
  id: number;
  ts: string;
  tipo: string;
  titulo: string;
  messageId: string | null;
}

/** Todo lo que el panel Expediente (§13) enseña de un contratado. Nada técnico. */
/**
 * 13-10 — la ficha de un agente (Pau: «al abrir un chat quiero saber quién es, qué hemos hecho, por qué está, qué está
 * haciendo, qué planea hacer y qué necesita»). La escribe el resumidor de la oficina (oficina/ficha.ts), no el agente.
 * «Qué necesita de ti» NO está aquí: sale de lo pendiente de verdad (pregunta, permiso, bloqueo), nunca de un resumen.
 */
export interface FichaDTO {
  porQue: string;
  hecho: string[];
  ahora: string;
  despues: string;
  /** Mensajes del agente que salieron en inglés → su traducción (id del mensaje → texto en español). */
  traducciones: Record<string, string>;
  /** Mensajes largos del agente → «en corto», 1-2 frases (Pau: «me da pena leerme estos textos»). */
  resumenes: Record<string, string>;
  /** Cuándo se escribió (ISO): la columna enseña su antigüedad. */
  en: string;
}

/** 13-10 — un archivo o foto de la conversación (Pau: «como en WhatsApp: ver todos los archivos y fotos»). */
export interface ArchivoDTO {
  messageId: string;
  quien: 'pau' | 'agente';
  en: string;
  id: string;
  nombre: string;
  tipo: string;
  tamano: number;
  imagen: boolean;
  /** 04-11 — lo que se lee en vez del nombre (título del PDF o el «# …» del informe); ausente si no hay. */
  titulo?: string;
  /** 04-11 — PDF: nº de páginas. */
  paginas?: number;
}

/** 13-10 — un mensaje que contiene lo buscado (Pau: «un filtro de búsqueda por palabras clave»). */
export interface ResultadoBusquedaDTO {
  messageId: string;
  quien: 'pau' | 'agente';
  en: string;
  texto: string;
}

export interface ExpedienteDTO {
  agentId: string;
  /** Lo que Pau pidió al contratar, tal cual (caja del formulario). */
  mision: string;
  paraQue: string;
  comoSabre: string;
  contratoEstado: ContratoEstado;
  /** El contrato propuesto (esquema ProponerContrato), o null si aún no lo ha propuesto. */
  contrato: Record<string, unknown> | null;
  pensando: string;
  siguientePaso: string;
  bloqueo: string;
  confianza: 'alta' | 'media' | 'baja' | null;
  criterios: CriterioDTO[];
  riesgos: string[];
  sinVerificar: string[];
  tareas: { texto: string; estado: 'pendiente' | 'en_curso' | 'hecha' }[];
  pregunta: PreguntaDTO | null;
  permisos: PermisoDTO[];
  historial: EntradaHistorialDTO[];
  /** 13-10 — la ficha (columna derecha) que escribe el resumidor; null hasta su primer resumen. */
  ficha: FichaDTO | null;
  /** Mensajes a este agente que no han llegado y ya no se reintentan solos (revisión R4). */
  sinEntregar: { messageId: string; clase: 'encargo' | 'orden' | 'pau'; texto: string; en: string; reenviable: boolean }[];
  /** Cerrar trabajo (§14): dónde va el cierre y lo que dejó el agente al irse. */
  cierre: {
    ciclo: Ciclo;
    /** El permiso «¿Integro?» de ESTE cierre (el único que la web ofrece para integrar). */
    permisoId: string | null;
    /** Mientras se comprueba, revisa o integra, no se le puede escribir (igual que el servidor). */
    bloqueaMensajes: boolean;
    paso: 'esperando_informe' | 'comprobando' | 'revisando' | 'esperando_permiso' | 'publicando' | 'en_cola' | 'archivando' | null;
    /** 30-09 — «integrar»: la oficina integra su trabajo y él sigue; «cerrar» (o null): se va. */
    modo: 'cerrar' | 'integrar' | null;
    motivoAtasco: string;
    integracion: 'pendiente' | 'integrada' | 'sin_integrar' | 'conflicto' | 'fallo' | null;
    /** Enlace a la propuesta de cambios (el único dato técnico que se enseña: un enlace para abrir). */
    enlace: string | null;
    cerradoEn: string | null;
    revision: { veredicto: 'ok' | 'con_reparos' | 'no'; resumen: string; huecos: string[] } | null;
    /** WS15-D8 — `modo`: «integrar» = su nota de entrega (integró una parte y sigue); «cerrar» = su informe de cierre (se fue). */
    informe: { modo: ModoInforme; resultado: string; noHecho: { que: string; por_que: string }[]; riesgos: string[]; siContinuas: string; gasto: { dolares: number; enQue: string } | null } | null;
  };
}

/**
 * 05-11 (medir el 11-11, #244/#249) — los eventos de vista de la oficina (POST /api/oficina/eventos, tabla
 * `oficina_evento`, migración 109): QUÉ hizo quien mira y DESDE DÓNDE. Sin contenido: solo el tipo, el origen y el agente.
 * El SQL para leerlos: web/qa/auditoria-apple/auditoria-final/medir-11-11.md.
 */
export const TIPOS_EVENTO = ['abrir_chat', 'alt_siguiente', 'abrir_info', 'abrir_carpeta', 'ctrl_k'] as const;
export type TipoEvento = (typeof TIPOS_EVENTO)[number];
export const ORIGENES_EVENTO = ['lista', 'mesa', 'ctrlk', 'alt', 'aviso', 'telegram'] as const;
export type OrigenEvento = (typeof ORIGENES_EVENTO)[number];

